<script setup lang="ts">
import { useTableStore } from '@/stores/tableStore.ts'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import HeaderFixedWidthIcon from '@/components/Interface/DataTable/Headers/HeaderFixedWidthIcon.vue'
import PlanetIcon from '@/components/Objects/Icons/PlanetIcon.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import HeaderFixedWidthTextNoSort from '@/components/Interface/DataTable/Headers/HeaderFixedWidthTextNoSort.vue'
import { useShipStore } from '@/stores/shipStore.ts'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import ActionBarSearch from '@/components/Interface/DataTable/ActionBar/ActionBarSearch.vue'
import ActionBarSelect from '@/components/Interface/DataTable/ActionBar/ActionBarSelect.vue'
import PlanetCompatibility from '@/components/Objects/PlanetCompatibility.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const mapCoordinateStore = useMapCoordinateStore();
const mapStore = useMapStore();
const shipStore = useShipStore();
const tableStore = useTableStore();

// Load filtered ships list
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filters = (coords: any[]) => {
  return coords
    .filter(coord => coord.playerId === 0)
    .filter((coord) => {
      // If no search query, return all
      if (!tableStore.mapCoords.filters.nameSearch || tableStore.mapCoords.filters.nameSearch === '') {
        return true;
      }
      // Otherwise, filter based on the ship name (case-insensitive search)
      return coord.name.toLowerCase().includes(tableStore.mapCoords.filters.nameSearch.toLowerCase());
    })
    .filter((coord) => {
      if(tableStore.mapCoords.filters.selectedCompatible === '') {
        return true;
      }

      if(tableStore.mapCoords.filters.selectedCompatible === coord.compatibility) {
        return true;
      }
    });
};

const onClick = (id:number) => {
  const data = mapCoordinateStore.planets.get(id);
  mapStore.focusMap(data?.xPos, data?.yPos);
};

</script>

<template>
  <FerionDataTable
    :data="mapCoordinateStore.planets"
    tableStoreName="mapCoords"
    :selector="false"
    :filters="filters"
    :heightLimit="'h-[calc(100vh-9rem)]'"
    :click="onClick"
  >
    <!--- ActionBar --->
    <template #actionBar>
      <ActionBarFiller class="mr-1 rounded">
        <div class="py-3">
          &nbsp;
        </div>
      </ActionBarFiller>
      <ActionBarSearch v-model="tableStore.mapCoords.filters.nameSearch" :name="t('map.sidebar.planets.name')" :placeHolder="t('map.sidebar.planets.search')" class="mr-1" width="16"/>
      <ActionBarSelect :name="t('map.sidebar.planets.compatibility')" width="6" v-model="tableStore.mapCoords.filters.selectedCompatible" class="" >
        <option value="" selected>{{ t('map.sidebar.planets.all') }}</option>
        <option value="A">{{ t('map.sidebar.planets.paradise') }}</option>
        <option value="B">{{ t('map.sidebar.planets.lush') }}</option>
        <option value="C">{{ t('map.sidebar.planets.habitable') }}</option>
        <option value="D">{{ t('map.sidebar.planets.incompatible') }}</option>
        <option value="E">{{ t('map.sidebar.planets.toxic') }}</option>
      </ActionBarSelect>
    </template>
    <!--- Headers --->
    <template #columns="{ sort, sortColumn, sortDirection }">
      <HeaderFixedWidthIcon width="4" column="type" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" class="rounded-tl">
        <div class="flex justify-center">
          <PlanetIcon :size="2" :type="5"></PlanetIcon>
        </div>
      </HeaderFixedWidthIcon>
      <HeaderTextFlex min-width="12"  column="name" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        {{ t('map.sidebar.planets.name') }}
      </HeaderTextFlex>
      <HeaderFixedWidthText width="6" column="eta" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" align="center" :title="t('map.sidebar.fleets.distanceTooltip')">
        {{ t('map.sidebar.planets.distance') }}
      </HeaderFixedWidthText>
      <HeaderFixedWidthText width="6" column="compatibility" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" align="center">
        {{ t('map.sidebar.planets.compatibility') }}
      </HeaderFixedWidthText>
      <HeaderFixedWidthTextNoSort width="3" align="center" class="rounded-tr">
        <GenericIcon type="cmd_settle" :tooltip="t('map.sidebar.planets.settlerInbound')"/>
      </HeaderFixedWidthTextNoSort>
    </template>
    <!--- Rows --->
    <template #rows="{ item }" >
      <CellFixedWidth width="4">
        <PlanetIcon :size="Number(item.type) === 8 ? 3 : item.size" :type="item.type"></PlanetIcon>
      </CellFixedWidth>
      <CellFlexWidth min-width="12">
        {{ item.name }}
      </CellFlexWidth>
      <CellFixedWidth width="6" align="center">
        <span class="text-subtext">{{ item.eta>0 && item.eta !== Infinity ? (item.eta >= 500 ? '500+' : item.eta) : '-' }}</span>
      </CellFixedWidth>
      <CellFixedWidth width="6" align="center">
        <PlanetCompatibility :compatibility="item.compatibility"/>
      </CellFixedWidth>
      <CellFixedWidth width="3" class="text-subtext" align="center">
        <GenericIcon v-if="shipStore.isPlanetTargetedForSettlement(item.xPos,item.yPos)" type="cmd_settle" :tooltip="t('map.sidebar.planets.settlerInbound')"/>
      </CellFixedWidth>
    </template>

  </FerionDataTable>
</template>

<style scoped lang="scss">

</style>